import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@car/ui/components/alert-dialog";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent } from "@car/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import { FormAlert, useAppForm, useStore } from "@car/ui/components/form";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@car/ui/components/tabs";
import { optionalText, requiredChoice, requiredText } from "@car/ui/lib/form-rules";
import {
  Call02Icon,
  Location01Icon,
  PackageCheckIcon,
  PlusSignIcon,
  TowTruckIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { branchMapLinks } from "@car/api/catalog-config";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader, PanelEmpty, PanelQueryError } from "@/components/panel-bits";
import { telHref, useSiteContent } from "@/lib/content";
import { faDate, faDateTimeShort, faNumber, faTime, faToman, toFaDigits } from "@/lib/format";
import {
  partsCancellableStatuses,
  partsDeliveryKeys,
  partsDeliveryLabels,
} from "@car/api/service-config";
import {
  partsAvailabilityLabels,
  partsInquiryStatusLabels,
  roadsideKindKeys,
  roadsideKindLabels,
  roadsideStatusLabels,
  type GarageVehicle,
  type PartsInquiry,
  type PartsInquiryStatusKey,
  type RoadsideKindKey,
  type RoadsideRequest,
  type RoadsideStatusKey,
} from "@/lib/panel";
import { TargetPayments } from "@/components/target-payments";
import { PayDialog } from "@/routes/pay/-pay-dialog";
import { orpc } from "@/utils/orpc";

import { SEP } from "@car/ui/lib/utils";

const isRoadsideKind = (value: string): value is RoadsideKindKey =>
  roadsideKindKeys.some((key) => key === value);

export const Route = createFileRoute("/panel/requests")({
  head: () => ({ meta: [{ title: "درخواست‌ها | پنل مشتریان تابان خودرو" }] }),
  component: RequestsPage,
});

const roadsideBadgeClass = {
  submitted: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  dispatched: "bg-primary/10 text-primary",
  done: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  cancelled: "bg-muted text-muted-foreground",
} satisfies Record<RoadsideStatusKey, string>;

const partsBadgeClass = {
  open: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  quoted: "bg-primary/10 text-primary",
  unavailable: "bg-destructive/10 text-destructive",
  ordered: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
  closed: "bg-muted text-muted-foreground",
  paid: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  ready: "bg-primary/10 text-primary",
  delivered: "bg-muted text-muted-foreground",
  refunded: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
} satisfies Record<PartsInquiryStatusKey, string>;

function RequestsPage() {
  const { contact } = useSiteContent();
  const query = useQuery(orpc.panel.requests.list.queryOptions());

  return (
    <div>
      <PageHeader
        title="درخواست‌ها"
        description="امداد جاده‌ای و استعلام قطعه اصلی را اینجا ثبت و پیگیری کنید."
      />

      {/* بدون شماره امداد در مدیریت محتوا، دکمه تماس فوری نمایش داده نمی‌شود */}
      {contact.hotlinePhone.trim() ? (
        <Alert className="mb-6">
          <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
          <AlertTitle>در شرایط اضطراری تماس بگیرید</AlertTitle>
          <AlertDescription className="grid gap-3">
            <p>تماس با امداد ۲۴ ساعته سریع‌تر از ثبت درخواست است؛ فرم برای پیگیری و سوابق است.</p>
            <EmergencyCallButton phone={contact.hotlinePhone} />
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="grid gap-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : query.isError ? (
        <PanelQueryError title="درخواست‌ها بارگیری نشد" query={query} />
      ) : (
        <Tabs defaultValue="roadside" className="grid gap-4">
          <TabsList>
            <TabsTrigger value="roadside">امداد جاده‌ای</TabsTrigger>
            <TabsTrigger value="parts">استعلام قطعه</TabsTrigger>
          </TabsList>

          <TabsContent value="roadside" className="grid gap-4">
            <RoadsideForm garage={query.data.garage} />
            {query.data.roadside.length > 0 ? (
              query.data.roadside.map((request) => (
                <RoadsideCard key={request.id} request={request} />
              ))
            ) : (
              <PanelEmpty
                icon={TowTruckIcon}
                title="درخواست امدادی ثبت نشده"
                description="اگر خودرو در راه ماند، از دکمه بالا درخواست بدهید تا نزدیک‌ترین واحد اعزام شود."
              />
            )}
          </TabsContent>

          <TabsContent value="parts" className="grid gap-4">
            <PartsForm garage={query.data.garage} />
            {query.data.parts.length > 0 ? (
              query.data.parts.map((inquiry) => <PartsCard key={inquiry.id} inquiry={inquiry} />)
            ) : (
              <PanelEmpty
                icon={PackageCheckIcon}
                title="استعلامی ثبت نشده"
                description="نام یا شماره فنی قطعه را بفرستید تا قیمت، موجودی و زمان تأمین را اعلام کنیم."
              />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/** دکمه تماس مستقیم با امداد؛ هدف لمسی بزرگ برای شرایط اضطراری */
function EmergencyCallButton({ phone }: { phone: string }) {
  return (
    <Button
      size="lg"
      className="min-h-11 justify-self-start"
      nativeButton={false}
      render={<a href={telHref(phone)} />}
    >
      <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
      تماس با امداد{" "}
      <span dir="ltr" className="font-bold">
        {toFaDigits(phone)}
      </span>
    </Button>
  );
}

type MapLink = { label: string; href: string };

/** پیوند موقعیت در نقشه‌های رایج (نشان، بلد، گوگل) */
function MapLinks({ links }: { links: MapLink[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      <span className="text-muted-foreground">موقعیت روی نقشه:</span>
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-6 items-center font-bold text-primary underline underline-offset-4"
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}

const cancelSchema = z.object({ reason: requiredText("دلیل لغو", { min: 3, max: 200 }) });

/** لغو با تأیید و دلیل؛ امدادگر دلیل را می‌بیند */
function CancelRoadsideDialog({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const mutation = useMutation(orpc.panel.requests.cancelRoadside.mutationOptions());
  const form = useAppForm({
    defaultValues: { reason: "" },
    validators: { onBlur: cancelSchema },
    onSubmit: async ({ value }) => {
      setError("");
      const result = await mutation
        .mutateAsync({ id, reason: value.reason.trim() })
        .catch(() => null);
      if (!result) {
        setError("لغو انجام نشد؛ دوباره تلاش کنید.");
        return;
      }
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      toast.success("درخواست امداد لغو شد");
      queryClient.invalidateQueries();
      setOpen(false);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          form.reset({ reason: "" });
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" className="justify-self-start" />}>
        لغو درخواست
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>درخواست امداد لغو شود؟</DialogTitle>
              <DialogDescription>
                اگر امدادگر در راه است، با لغو برمی‌گردد. لطفاً دلیل را بنویسید.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="reason">
              {(field) => (
                <field.TextareaField
                  label="دلیل لغو"
                  maxLength={200}
                  rows={2}
                  placeholder="مثلاً خودرو روشن شد و راه افتادم."
                />
              )}
            </form.AppField>
            <FormAlert message={error} />
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>بله، لغو شود</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function RoadsideCard({ request }: { request: RoadsideRequest }) {
  const active = request.status === "submitted" || request.status === "dispatched";

  return (
    <Card>
      <CardContent className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-bold">
            {roadsideKindLabels[request.kind]}{" "}
            <span className="font-normal text-muted-foreground">— {request.vehicleName}</span>
          </span>
          <Badge className={roadsideBadgeClass[request.status]}>
            {roadsideStatusLabels[request.status]}
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">{request.location}</p>
        {request.mapLinks.length > 0 ? <MapLinks links={request.mapLinks} /> : null}
        {request.note ? <p className="text-sm">{request.note}</p> : null}
        {request.arrivalIso ? (
          <p className="text-sm font-bold text-primary">
            رسیدن امدادگر حدود ساعت {faTime(request.arrivalIso)}
          </p>
        ) : null}
        {request.status === "cancelled" && request.cancelReason ? (
          <p className="text-sm">
            <span className="text-muted-foreground">
              {request.cancelledBy === "staff" ? "دلیل لغو از طرف امداد: " : "دلیل لغو: "}
            </span>
            {request.cancelReason}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          ثبت {faDateTimeShort(request.createdIso)}
          {request.technician ? `${SEP}${request.technician}` : ""}
          {request.status === "done" && request.closedIso
            ? `${SEP}انجام شد ${faDateTimeShort(request.closedIso)}`
            : ""}
        </p>
        {active ? <CancelRoadsideDialog id={request.id} /> : null}
      </CardContent>
    </Card>
  );
}

function PartsCard({ inquiry }: { inquiry: PartsInquiry }) {
  return (
    <Card>
      <CardContent className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-bold">
            {inquiry.partName}{" "}
            <span className="font-normal text-muted-foreground">— {inquiry.vehicleName}</span>
          </span>
          <Badge className={partsBadgeClass[inquiry.status]}>
            {partsInquiryStatusLabels[inquiry.status]}
          </Badge>
        </div>
        {inquiry.partNumber ? (
          <p dir="ltr" className="text-start font-mono text-xs text-muted-foreground">
            {inquiry.partNumber}
          </p>
        ) : null}
        {inquiry.note ? <p className="text-sm">{inquiry.note}</p> : null}
        {inquiry.status === "ordered" || inquiry.status === "unavailable" ? (
          <div
            className={
              inquiry.status === "ordered"
                ? "grid gap-1 rounded-lg bg-sky-500/10 p-3 text-sm"
                : "grid gap-1 rounded-lg bg-destructive/10 p-3 text-sm"
            }
          >
            <p className="font-bold">
              {inquiry.status === "ordered"
                ? inquiry.expectedIso
                  ? `قطعه از تأمین‌کننده سفارش داده شد؛ رسیدن حدود ${faDate(inquiry.expectedIso)}.`
                  : "قطعه از تأمین‌کننده سفارش داده شد."
                : "این قطعه در حال حاضر موجود نیست."}
            </p>
            {inquiry.statusNote ? <p>{inquiry.statusNote}</p> : null}
          </div>
        ) : null}
        {/* پس از ناموجود شدن، قیمت قبلی دیگر معتبر نیست */}
        {inquiry.quote && inquiry.status !== "unavailable" ? (
          <dl className="grid gap-x-6 gap-y-1 rounded-lg bg-muted/60 p-3 text-sm sm:grid-cols-2">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">قیمت اعلام‌شده</dt>
              <dd className="font-bold">{faToman(inquiry.quote.price)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">موجودی</dt>
              <dd>{partsAvailabilityLabels[inquiry.quote.availability]}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">زمان تأمین</dt>
              <dd>{faNumber(inquiry.quote.leadTimeDays)} روز کاری</dd>
            </div>
            {inquiry.status === "quoted" ? (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">اعتبار قیمت</dt>
                <dd>{faDate(inquiry.quote.validUntilIso)}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}
        {inquiry.status === "quoted" && inquiry.quote ? (
          new Date(inquiry.quote.validUntilIso).getTime() >= Date.now() ? (
            <div className="justify-self-start">
              <PayDialog
                purpose="parts_quote"
                targetId={inquiry.id}
                title={`قطعه ${inquiry.partName}`}
              >
                پرداخت و ثبت سفارش قطعه
              </PayDialog>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              اعتبار این قیمت تمام شده است؛ برای قیمت تازه با ما تماس بگیرید.
            </p>
          )
        ) : null}
        {inquiry.status === "refunded" ? (
          <div className="grid gap-1 rounded-lg bg-sky-500/10 p-3 text-sm">
            <p className="font-bold">وجه این قطعه بازگردانده شد.</p>
            {inquiry.statusNote ? <p>{inquiry.statusNote}</p> : null}
          </div>
        ) : inquiry.payment ? (
          <div className="grid gap-1 rounded-lg bg-emerald-500/10 p-3 text-sm">
            <p className="font-bold text-emerald-700 dark:text-emerald-400">
              {inquiry.status === "delivered"
                ? "پرداخت شد و قطعه تحویل شد"
                : inquiry.status === "ready"
                  ? inquiry.delivery.method === "courier"
                    ? "قطعه برای ارسال به نشانی شما آماده شد"
                    : `قطعه آماده تحویل است${inquiry.delivery.branchName ? ` در ${inquiry.delivery.branchName}` : ""}`
                  : "پرداخت شد؛ سفارش قطعه ثبت شد و آماده شدنش پیامک می‌شود"}
            </p>
            {inquiry.status === "ready" && inquiry.statusNote ? <p>{inquiry.statusNote}</p> : null}
            <p>
              {faToman(inquiry.payment.amount)}
              {SEP}
              {faDateTimeShort(inquiry.payment.paidIso)}
            </p>
            {inquiry.payment.reference ? (
              <p className="text-muted-foreground">
                کد پیگیری:{" "}
                <span dir="ltr" className="font-mono font-bold text-foreground">
                  {toFaDigits(inquiry.payment.reference)}
                </span>
              </p>
            ) : null}
          </div>
        ) : null}
        {inquiry.quote || inquiry.payment ? <DeliveryRow inquiry={inquiry} /> : null}
        <TargetPayments purpose="parts_quote" targetId={inquiry.id} title="پرداخت‌های این قطعه" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">ثبت {faDateTimeShort(inquiry.createdIso)}</p>
          {partsCancellableStatuses.some((status) => status === inquiry.status) &&
          !inquiry.payment ? (
            <CancelPartsButton inquiry={inquiry} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** «لغو استعلام» با تأیید؛ فقط تا وقتی چیزی پرداخت نشده است */
function CancelPartsButton({ inquiry }: { inquiry: PartsInquiry }) {
  const queryClient = useQueryClient();
  const cancel = useMutation(
    orpc.panel.requests.cancelPartsInquiry.mutationOptions({
      onSuccess: (result) => {
        if (!result.ok) {
          toast.error(result.reason);
          return;
        }
        toast.success("استعلام لغو شد");
        queryClient.invalidateQueries();
      },
      onError: () => toast.error("لغو انجام نشد؛ دوباره تلاش کنید."),
    }),
  );
  return (
    <AlertDialog>
      <AlertDialogTrigger render={<Button variant="ghost" size="sm" disabled={cancel.isPending} />}>
        لغو استعلام
      </AlertDialogTrigger>
      <AlertDialogContent lang="fa" dir="rtl">
        <AlertDialogHeader>
          <AlertDialogTitle>استعلام «{inquiry.partName}» لغو شود؟</AlertDialogTitle>
          <AlertDialogDescription>
            قیمت اعلام‌شده دیگر قابل پرداخت نیست؛ برای خرید دوباره باید استعلام تازه ثبت کنید.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>انصراف</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={() => cancel.mutate({ id: inquiry.id })}
          >
            لغو استعلام
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** محل و روش تحویل قطعه؛ تا آماده شدن قطعه قابل تغییر */
function DeliveryRow({ inquiry }: { inquiry: PartsInquiry }) {
  const { delivery } = inquiry;
  const editable = ["open", "quoted", "ordered", "paid"].includes(inquiry.status);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm">
      <span className="grid gap-0.5">
        <span className="font-bold">{partsDeliveryLabels[delivery.method]}</span>
        <span className="text-muted-foreground">
          {delivery.method === "courier"
            ? delivery.address || "نشانی ارسال را وارد کنید."
            : delivery.branchName
              ? `${delivery.branchName}${delivery.branchAddress ? `، ${delivery.branchAddress}` : ""}`
              : "شعبه تحویل را کارشناس اعلام می‌کند یا خودتان انتخاب کنید."}
        </span>
      </span>
      {editable ? <DeliveryDialog inquiry={inquiry} /> : null}
    </div>
  );
}

const deliveryOptions = partsDeliveryKeys.map((key) => ({
  value: key,
  label: partsDeliveryLabels[key],
}));

function DeliveryDialog({ inquiry }: { inquiry: PartsInquiry }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const branches = useQuery({ ...orpc.site.branches.queryOptions(), enabled: open });
  const mutation = useMutation(orpc.panel.requests.setPartsDelivery.mutationOptions());
  const initial = {
    method: String(inquiry.delivery.method),
    branchId: inquiry.delivery.branchId ?? "",
    address: inquiry.delivery.address,
  };
  const form = useAppForm({
    defaultValues: initial,
    validators: {
      onBlur: z.object({
        method: requiredChoice("روش تحویل"),
        branchId: z.string(),
        address: optionalText("نشانی", 300),
      }),
    },
    onSubmit: async ({ value }) => {
      setError("");
      const result = await mutation
        .mutateAsync({
          id: inquiry.id,
          method: value.method === "courier" ? "courier" : "pickup",
          branchId: value.branchId || null,
          address: value.address.trim(),
        })
        .catch(() => null);
      if (!result) return setError("ذخیره انجام نشد؛ دوباره تلاش کنید.");
      if (!result.ok) return setError(result.reason);
      toast.success("روش تحویل ذخیره شد");
      queryClient.invalidateQueries();
      setOpen(false);
    },
  });
  const method = useStore(form.store, (state) => state.values.method);
  const branchOptions = [
    { value: "", label: "انتخاب شعبه" },
    ...(branches.data?.branches ?? []).map((item) => ({ value: item.id, label: item.name })),
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          form.reset(initial);
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" />}>تغییر روش تحویل</DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>تحویل قطعه «{inquiry.partName}»</DialogTitle>
              <DialogDescription>
                قطعه را در شعبه تحویل بگیرید یا نشانی بدهید تا ارسال شود؛ هزینه ارسال هنگام تحویل
                پرداخت می‌شود.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="method">
              {(field) => <field.SelectField label="روش تحویل" options={deliveryOptions} />}
            </form.AppField>
            {method === "courier" ? (
              <form.AppField name="address">
                {(field) => (
                  <field.TextareaField
                    label="نشانی کامل ارسال"
                    rows={3}
                    maxLength={300}
                    placeholder="شهر، خیابان، پلاک، واحد و کد پستی"
                  />
                )}
              </form.AppField>
            ) : (
              <form.AppField name="branchId">
                {(field) => <field.SelectField label="شعبه تحویل" options={branchOptions} />}
              </form.AppField>
            )}
            <FormAlert message={error} />
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>ذخیره</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

const garageOptions = (garage: GarageVehicle[]) =>
  garage.map((item) => ({ value: item.owned.id, label: item.vehicle.name }));

/** خودرو پیش‌فرض اولین خودروی گاراژ است؛ گاراژ خالی یعنی درخواست ممکن نیست */
const vehicleRule = (what: string) =>
  z.string().min(1, `برای ثبت ${what} باید خودرویی به نام شما ثبت شده باشد.`);

// محل: موقعیت گوشی یا نشانی نوشته‌شده (دست‌کم ۵ نویسه)؛ نبودِ هر دو را onSubmit و سرور رد می‌کنند
const roadsideSchema = z.object({
  ownedVehicleId: vehicleRule("درخواست"),
  kind: requiredChoice("نوع مشکل"),
  location: optionalText("نشانی محل خودرو", 200),
  note: optionalText("توضیح", 500),
});

type PhonePosition = { latitude: number; longitude: number };

/** موقعیت فعلی گوشی با Geolocation مرورگر؛ خطا یعنی مشتری نشانی را می‌نویسد */
function usePhonePosition() {
  const [position, setPosition] = useState<PhonePosition | null>(null);
  const [locating, setLocating] = useState(false);
  const [problem, setProblem] = useState("");
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setProblem("این مرورگر موقعیت را نمی‌دهد؛ نشانی را بنویسید.");
      return;
    }
    setLocating(true);
    setProblem("");
    navigator.geolocation.getCurrentPosition(
      (found) => {
        // شش رقم اعشار (حدود ده سانتی‌متر) کافی است
        const round = (value: number) => Math.round(value * 1e6) / 1e6;
        setPosition({
          latitude: round(found.coords.latitude),
          longitude: round(found.coords.longitude),
        });
        setLocating(false);
      },
      (failure) => {
        setLocating(false);
        setProblem(
          failure.code === failure.PERMISSION_DENIED
            ? "اجازه دسترسی به موقعیت داده نشد؛ نشانی را بنویسید یا از تنظیمات مرورگر اجازه دهید."
            : "موقعیت پیدا نشد؛ دوباره تلاش کنید یا نشانی را بنویسید.",
        );
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  };
  const reset = () => {
    setPosition(null);
    setProblem("");
  };
  return { position, locating, problem, locate, reset };
}

const roadsideKindOptions = roadsideKindKeys.map((key) => ({
  value: key,
  label: roadsideKindLabels[key],
}));

function RoadsideForm({ garage }: { garage: GarageVehicle[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const { contact } = useSiteContent();
  const phone = usePhonePosition();

  const mutation = useMutation(orpc.panel.requests.createRoadside.mutationOptions());

  const initial = {
    ownedVehicleId: garage[0]?.owned.id ?? "",
    kind: "tow",
    location: "",
    note: "",
  };

  const form = useAppForm({
    defaultValues: initial,
    validators: { onBlur: roadsideSchema },
    onSubmit: async ({ value }) => {
      if (!isRoadsideKind(value.kind)) return;
      const location = value.location.trim();
      if (!phone.position && location.length < 5) {
        setError("موقعیت فعلی را بفرستید یا نشانی محل خودرو را بنویسید.");
        return;
      }
      setError("");
      const result = await mutation
        .mutateAsync({
          ownedVehicleId: value.ownedVehicleId,
          kind: value.kind,
          location,
          position: phone.position,
          note: value.note.trim(),
        })
        .catch(() => null);
      if (!result) {
        setError("درخواست ثبت نشد؛ اتصال را بررسی کنید و دوباره بفرستید، یا با امداد تماس بگیرید.");
        return;
      }
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      toast.success("درخواست امداد ثبت شد", {
        description: "درخواست به کارشناسان امداد رسید؛ اعزام و زمان رسیدن را همین‌جا می‌بینید.",
      });
      queryClient.invalidateQueries();
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // هر بار باز شدن، فرم از اولین خودرو و فیلدهای خالی شروع می‌کند
        if (next) {
          form.reset(initial);
          setError("");
          phone.reset();
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button className="justify-self-start" />}>
        <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
        درخواست امداد جاده‌ای
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>درخواست امداد جاده‌ای</DialogTitle>
              <DialogDescription>
                نوع مشکل را انتخاب کنید و موقعیت فعلی را بفرستید یا نشانی را بنویسید.
              </DialogDescription>
            </DialogHeader>
            {contact.hotlinePhone.trim() ? (
              <EmergencyCallButton phone={contact.hotlinePhone} />
            ) : null}
            <div className="grid gap-4">
              <form.AppField name="ownedVehicleId">
                {(field) => <field.SelectField label="خودرو" options={garageOptions(garage)} />}
              </form.AppField>
              <form.AppField name="kind">
                {(field) => <field.SelectField label="نوع مشکل" options={roadsideKindOptions} />}
              </form.AppField>
              <div className="grid gap-2 rounded-lg border p-3">
                <Button
                  type="button"
                  variant={phone.position ? "outline" : "default"}
                  className="min-h-11"
                  disabled={phone.locating}
                  onClick={phone.locate}
                >
                  {phone.locating ? (
                    <Spinner className="size-4" />
                  ) : (
                    <HugeiconsIcon icon={Location01Icon} strokeWidth={1.8} />
                  )}
                  {phone.position ? "به‌روزرسانی موقعیت فعلی" : "ارسال موقعیت فعلی من"}
                </Button>
                <p
                  aria-live="polite"
                  className={
                    phone.problem ? "text-xs text-destructive" : "text-xs text-muted-foreground"
                  }
                >
                  {phone.locating
                    ? "در حال گرفتن موقعیت گوشی…"
                    : phone.position
                      ? "موقعیت گوشی ثبت شد؛ امدادگر آن را روی نقشه می‌بیند."
                      : phone.problem || "اگر موقعیت را نمی‌فرستید، نشانی را پایین بنویسید."}
                </p>
                {phone.position ? (
                  <MapLinks
                    links={branchMapLinks(phone.position.latitude, phone.position.longitude)}
                  />
                ) : null}
              </div>
              <form.AppField name="location">
                {(field) => (
                  <field.TextField
                    label={phone.position ? "نشانی یا نشانه محل (اختیاری)" : "نشانی محل خودرو"}
                    maxLength={200}
                    placeholder="مثلاً: اتوبان تهران–قم، بعد از عوارضی، لاین کندرو"
                  />
                )}
              </form.AppField>
              <form.AppField name="note">
                {(field) => (
                  <field.TextareaField
                    label="توضیح (اختیاری)"
                    maxLength={500}
                    rows={3}
                    placeholder="هر نکته‌ای که به امدادگر کمک می‌کند."
                  />
                )}
              </form.AppField>
              <FormAlert message={error} />
            </div>
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>ثبت درخواست</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

const partsSchema = z.object({
  ownedVehicleId: vehicleRule("استعلام"),
  partName: requiredText("نام قطعه", { min: 3, max: 120 }),
  partNumber: optionalText("شماره فنی", 40),
  note: optionalText("توضیح", 500),
});

function PartsForm({ garage }: { garage: GarageVehicle[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");

  const mutation = useMutation(orpc.panel.requests.createPartsInquiry.mutationOptions());

  const initial = {
    ownedVehicleId: garage[0]?.owned.id ?? "",
    partName: "",
    partNumber: "",
    note: "",
  };

  const form = useAppForm({
    defaultValues: initial,
    validators: { onBlur: partsSchema },
    onSubmit: async ({ value }) => {
      setError("");
      const result = await mutation
        .mutateAsync({
          ownedVehicleId: value.ownedVehicleId,
          partName: value.partName.trim(),
          partNumber: value.partNumber.trim(),
          note: value.note.trim(),
        })
        .catch(() => null);
      if (!result) return;
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      toast.success("استعلام قطعه ثبت شد", {
        description: "قیمت، موجودی و زمان تأمین حداکثر تا یک روز کاری اعلام می‌شود.",
      });
      queryClient.invalidateQueries();
      setOpen(false);
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // هر بار باز شدن، فرم از اولین خودرو و فیلدهای خالی شروع می‌کند
        if (next) {
          form.reset(initial);
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button className="justify-self-start" />}>
        <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
        استعلام قطعه اصلی
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>استعلام قطعه اصلی</DialogTitle>
              <DialogDescription>
                سازگاری، قیمت و زمان تأمین قطعه را کارشناس قطعات بررسی و اعلام می‌کند.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4">
              <form.AppField name="ownedVehicleId">
                {(field) => <field.SelectField label="خودرو" options={garageOptions(garage)} />}
              </form.AppField>
              <form.AppField name="partName">
                {(field) => (
                  <field.TextField
                    label="نام قطعه"
                    maxLength={120}
                    placeholder="مثلاً: لنت ترمز جلو"
                  />
                )}
              </form.AppField>
              <form.AppField name="partNumber">
                {(field) => (
                  <field.TextField
                    label="شماره فنی (اختیاری)"
                    dir="ltr"
                    className="text-start"
                    maxLength={40}
                    placeholder="AT7-0000-X"
                  />
                )}
              </form.AppField>
              <form.AppField name="note">
                {(field) => (
                  <field.TextareaField
                    label="توضیح (اختیاری)"
                    maxLength={500}
                    rows={3}
                    placeholder="شرایط خودرو، رنگ یا هر نکته‌ای که در انتخاب قطعه مؤثر است."
                  />
                )}
              </form.AppField>
              <FormAlert message={error} />
            </div>
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>ثبت استعلام</form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
